/**
 * Where the session runs, read off the block that announces it.
 *
 * A transcript is full of paths the chat *computed* rather than read off a
 * tool's arguments: the file a `cat`/`sed` row was rewritten from
 * ({@link ./shell-file-ops.ts}), the operand a summarized shell row names
 * ({@link ./shell-summary.ts}), the path on a diff or read card. Agents type
 * those relative — `plugins/plugin-chat/src/web/…` — while the file-preview
 * affordance is reached through the content-scanner registry, which only claims
 * *absolute* paths. `@reflex/ui`'s `WorkingDirProvider` closes that gap for
 * every path-rendering surface; this module is the one thing only chat can
 * supply, which is the directory itself.
 *
 * Claude's `system`/`init` and Codex's `thread/start` both announce a `cwd`, and
 * {@link useBlockManager} captures it as the resulting `system_init` blocks
 * arrive. ACP (OpenCode) announces none, so its relative paths keep the plain
 * rendering rather than being resolved against a guess.
 */
import type { SystemInitBlock } from './types.js';

/** The working directory a session-init block announced, if its protocol has one. */
export function initWorkingDir(block: SystemInitBlock): string | null {
  const extensions = block.extensions;
  if (!extensions) return null;
  // ACP's init payload carries no working directory, so its `protocol` arm has
  // no `cwd` field to read.
  if (extensions.protocol !== 'claude' && extensions.protocol !== 'codex') return null;
  const cwd = extensions.cwd;
  // Anything but an absolute directory would make every path resolved against
  // it wrong, so it is no better than not knowing.
  return cwd?.startsWith('/') ? cwd : null;
}
